<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from "vue";
import { RULES, replay, strikeWouldPenalise, validateBank, type Turn } from "../../domain/rules.js";
import { createWakeLock } from "../../composables/wakeLock.js";
import GameLeaderboard from "./GameLeaderboard.vue";
import ScoreKeypad from "./ScoreKeypad.vue";
import SyncBadge from "../sync/SyncBadge.vue";
import type { SyncStatus } from "../../sync/types.js";

/**
 * Live scoring, PRD 6.2 -- the most-used screen in the app.
 *
 * The whole screen fills the viewport and never scrolls as a unit: the
 * leaderboard is capped at 31 dvh and scrolls internally, the keypad is pinned
 * at 44 dvh. Everything derives from the turn log via `replay`, so nothing here
 * keeps a second copy of the score that could drift out of step.
 */

const props = defineProps<{
  order: string[];
  names: Record<string, string>;
  turns: Turn[];
  status: SyncStatus;
  /** The six quick-entry scores, learned from this group's history. */
  quick: number[];
  /** Device preference: hold a screen wake lock while scoring. */
  keepAwake: boolean;
}>();

const emit = defineEmits<{
  bank: [points: number];
  strike: [];
  undo: [];
  back: [];
}>();

const entry = ref("");

const state = computed(() => replay(props.order, props.turns));
const currentName = computed(() =>
  state.value.current ? (props.names[state.value.current] ?? state.value.current) : "",
);

const value = computed(() => (entry.value === "" ? 0 : Number(entry.value)));
const validity = computed(() => validateBank(value.value));
const canBank = computed(() => !state.value.finished && validity.value.ok);

/** FR-3.4/3.5: say which condition fails and how far off it is, or preview the new total. */
const hint = computed<string>(() => {
  const current = state.value.current;
  if (state.value.finished || !current) return "Spiel beendet";
  if (entry.value === "") return "Punkte eingeben oder Strich";

  const v = value.value;
  if (v < RULES.minBank) return `Noch ${RULES.minBank - v} bis ${RULES.minBank}`;
  if (v % RULES.granularity !== 0) return `Muss durch ${RULES.granularity} teilbar sein`;

  const player = state.value.players.find((p) => p.playerId === current);
  const next = (player?.total ?? 0) + v;
  return `Neuer Stand: ${next.toLocaleString("de-DE")}`;
});

/** FR-3.7: warn before the tap, not after the penalty. */
const strikeLabel = computed(() => {
  const current = state.value.current;
  if (current && strikeWouldPenalise(state.value, current)) return `Strich −${RULES.penalty}`;
  return "Strich";
});

const bankLabel = computed(() =>
  entry.value === "" ? "Eintragen" : `${value.value.toLocaleString("de-DE")} eintragen`,
);

function digit(d: string): void {
  // FR-3.3: capped at six digits. A leading zero is meaningless here.
  if (entry.value.length >= 6) return;
  if (entry.value === "" && d === "0") return;
  entry.value += d;
}

function bank(): void {
  if (!canBank.value) return;
  emit("bank", value.value);
  entry.value = "";
}

/** FR-3.6: quick entries book immediately, with no confirmation step. */
function bankQuick(points: number): void {
  if (state.value.finished) return;
  emit("bank", points);
  entry.value = "";
}

function strike(): void {
  if (state.value.finished) return;
  emit("strike");
  entry.value = "";
}

// FR-3.6, reworked: the six scores this group banks most, learned from history
// and passed in. The sub-labels went with 1500/2000 -- a learned number needs
// no gloss.

// Keep the screen awake while this screen is up. A scorekeeper sits open on the
// table between turns; the phone locking every half minute is the annoyance.
// Held only here -- once the game ends or the app moves elsewhere, the screen
// returns to its normal timeout. A silent no-op where the API is missing.
const wakeLock =
  typeof navigator !== "undefined" && typeof document !== "undefined"
    ? createWakeLock(navigator, document)
    : null;
// Follow the preference live: flipping the switch mid-game takes effect at once.
watch(
  () => props.keepAwake,
  (on) => void (on ? wakeLock?.enable() : wakeLock?.disable()),
  { immediate: true },
);
onBeforeUnmount(() => void wakeLock?.disable());
</script>

<template>
  <div class="screen">
    <header class="nav">
      <button
        class="nav-btn"
        @click="emit('back')"
      >
        ‹ Zurück
      </button>
      <div class="round">
        <SyncBadge :status="props.status" />
        <span class="num">Runde {{ state.round }}</span>
        <span
          v-if="state.isFinalRound && !state.finished"
          class="final"
        >Letzte Runde</span>
      </div>
      <button
        class="nav-btn"
        :disabled="props.turns.length === 0"
        @click="emit('undo')"
      >
        Zurücknehmen
      </button>
    </header>

    <GameLeaderboard
      :standings="state.standings"
      :names="props.names"
      :current="state.current"
      :finished="state.finished"
    />

    <div class="player-bar">
      <span class="player-name">{{ currentName }}</span>
      <span class="entry">
        <span class="entry-value num">{{ entry === "" ? "0" : value.toLocaleString("de-DE") }}</span>
        <span class="hint">{{ hint }}</span>
      </span>
    </div>

    <div class="quick">
      <button
        v-for="q in props.quick"
        :key="q"
        class="quick-btn"
        :disabled="state.finished"
        @click="bankQuick(q)"
      >
        <span class="num">{{ q }}</span>
      </button>
    </div>

    <ScoreKeypad
      :can-bank="canBank"
      :bank-label="bankLabel"
      :strike-label="strikeLabel"
      @digit="digit"
      @backspace="entry = entry.slice(0, -1)"
      @clear="entry = ''"
      @bank="bank"
      @strike="strike"
    />
  </div>
</template>

<style scoped>
.screen {
  /* dvh, not vh: the mobile browser chrome collapses on scroll and vh would
     leave the keypad hanging below the fold. */
  height: 100dvh;
  display: flex;
  flex-direction: column;
  /* PRD 6.2: the screen as a whole does not scroll. */
  overflow: hidden;
  padding-left: var(--safe-left);
  padding-right: var(--safe-right);
}

.nav {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: calc(var(--safe-top) + 6px) 12px 6px;
  min-height: 52px;
}

.nav-btn {
  background: none;
  border: none;
  color: var(--accent);
  font-size: 15px;
  padding: 8px 4px;
  min-height: 44px;
}

.nav-btn:disabled {
  color: var(--text-3);
}

.round {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  font-size: 15px;
  font-weight: 600;
  min-width: 0;
}

.final {
  font-size: 11px;
  font-weight: 500;
  color: var(--accent);
}

.player-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 6px 16px;
  min-height: 54px;
}

.player-name {
  font-size: 17px;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.entry {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  min-width: 0;
}

.entry-value {
  font-size: 26px;
  font-weight: 700;
  line-height: 1.1;
}

.hint {
  font-size: 12px;
  color: var(--text-2);
}

.quick {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 6px;
  padding: 0 12px 8px;
  min-height: 48px;
}

.quick-btn {
  border: none;
  border-radius: 10px;
  background: var(--card);
  padding: 6px 2px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1px;
  font-size: 13px;
  font-weight: 600;
  min-height: 44px;
}

.quick-btn:disabled {
  opacity: 0.4;
}

.quick-sub {
  font-size: 9px;
  font-weight: 500;
  color: var(--text-2);
  white-space: nowrap;
}
</style>
